<template>
  <div class="min-h-screen bg-beige-50 px-4 py-6 safe-area-top safe-area-bottom">
    <div class="max-w-md mx-auto">
      <!-- Header -->
      <div class="text-center mb-8 animate-fade-in">
        <div class="flex items-center justify-center mb-4">
          <div class="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
            <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
        </div>
        <h1 class="text-2xl font-semibold text-warm-900 mb-2">
          <span v-if="store.customerName && store.customerName !== 'Anonymous'">Thank you so much, {{ customerName }}!</span>
          <span v-else>Thank you so much!</span>
        </h1>
        <p class="text-warm-600">
          We're thrilled you had a great experience!
        </p>
      </div>

      <!-- AI Response Card -->
      <div v-if="aiResponse" class="card p-6 mb-6 animate-slide-up">
        <div class="flex items-start space-x-3">
          <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
            </svg>
          </div>
          <div class="flex-1">
            <p class="text-warm-700 text-sm leading-relaxed">{{ aiResponse }}</p>
          </div>
        </div>
      </div>

      <!-- Review Request Card -->
      <div class="card p-6 mb-6 animate-slide-up" style="animation-delay: 0.1s">
        <h2 class="text-lg font-semibold text-warm-900 mb-3">
          Would you mind sharing your positive experience with others?
        </h2>
        <p class="text-warm-600 text-sm mb-4 leading-relaxed">
          Your review helps other customers discover our service and supports our small business. 
          We would be incredibly grateful for just a minute of your time.
        </p>

        <!-- Review Buttons -->
        <div class="space-y-3">
          <a
            :href="googleReviewUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center justify-center w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-xl shadow-lg active:scale-95 transition-all duration-200"
            @click="trackReviewClick('google')"
          >
            <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            ⭐ Leave Google Review
          </a>
          
          <a
            href="https://www.yelp.com/writeareview/biz/f5RIwDea1mCuDKdwZwrMog?return_url=%2Fbiz%2Ff5RIwDea1mCuDKdwZwrMog&review_origin=biz-details-war-button"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center justify-center w-full py-3 px-4 bg-red-600 text-white font-medium rounded-xl shadow-lg active:scale-95 transition-all duration-200"
            @click="trackReviewClick('yelp')"
          >
            <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.004 5.367 18.637.001 12.017.001zM8.449 19.544c-.456.456-1.197.456-1.653 0L5.143 17.89c-.456-.456-.456-1.197 0-1.653l1.653-1.653c.456-.456 1.197-.456 1.653 0l1.653 1.653c.456.456.456 1.197 0 1.653l-1.653 1.654zm7.098-7.098c-.456.456-1.197.456-1.653 0L12.24 10.792c-.456-.456-.456-1.197 0-1.653l1.654-1.653c.456-.456 1.197-.456 1.653 0l1.653 1.653c.456.456.456 1.197 0 1.653l-1.653 1.654z"/>
            </svg>
            ⭐ Leave Yelp Review
          </a>
        </div>

        <!-- Alternative Options -->
        <div class="flex justify-center space-x-6 mt-6 text-sm">
          <button
            @click="handleMaybeLater"
            class="text-warm-500 hover:text-warm-600 transition-colors"
          >
            Maybe later
          </button>
          <button
            @click="handleNoThanks"
            class="text-warm-400 hover:text-warm-500 transition-colors"
          >
            No thanks
          </button>
        </div>
      </div>

      <!-- Phone Number Input for Anonymous Users -->
      <div v-if="shouldShowPhoneInput" class="card p-6 mb-6 animate-slide-up">
        <div class="text-center mb-4">
          <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-warm-900 mb-2">
            Please provide your mobile phone number to complete your feedback
          </h3>
        </div>
        
        <div class="space-y-4">
          <div>
            <input
              v-model="phoneNumber"
              type="tel"
              placeholder="e.g., 306-123-4567"
              class="w-full px-4 py-3 border border-warm-200 rounded-xl focus:ring-2 focus:ring-warm-300 focus:border-warm-300 transition-colors text-center text-lg"
              :disabled="isSubmittingPhone"
            />
          </div>
          
          <button
            @click="submitPhoneNumber"
            :disabled="!phoneNumber.trim() || isSubmittingPhone"
            class="w-full py-3 px-4 bg-warm-600 text-white font-medium rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-all duration-200"
          >
            <span v-if="isSubmittingPhone" class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </span>
            <span v-else>Submit</span>
          </button>
          
          <div v-if="phoneSubmitted" class="text-center">
            <div class="inline-flex items-center px-4 py-2 bg-green-100 rounded-lg">
              <svg class="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              <span class="text-green-800 text-sm font-medium">Phone number registered successfully!</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Incentive Card -->
      <div class="card p-6 animate-slide-up" style="animation-delay: 0.2s">
        <div class="text-center">
          <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
            </svg>
          </div>
          <h3 class="font-semibold text-warm-900 mb-2">
            As a thank you, here's 15% off your next engine oil service! (Valid for 5 months)
          </h3>
          <div class="bg-warm-50 rounded-xl p-6 border border-warm-200 animate-scale-in" style="animation-delay: 0.3s">
            <div class="bg-warm-100 rounded-lg p-3 mb-3">
              <code class="text-warm-800 font-mono text-lg">ENGINE15</code>
            </div>
            <p class="text-warm-600 text-xs">
              Valid until {{ expiryDate }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Google Review Popup -->
    <div 
      v-if="showReviewPopup"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in"
      @click="closePopup"
    >
      <div 
        class="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 animate-scale-in"
        @click.stop
      >
        <div class="text-center">
          <!-- Google Logo Icon -->
          <div class="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg border border-gray-100">
            <svg class="w-10 h-10" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
              <path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
              <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
              <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
            </svg>
          </div>

          <!-- Title -->
          <h3 class="text-xl font-semibold text-gray-900 mb-3">
            ⭐ Quick Google Review?
          </h3>

          <!-- Message -->
          <p class="text-gray-600 text-sm leading-relaxed mb-6">
            Would you mind taking just 30 seconds to leave a ⭐⭐⭐⭐⭐ Google review? 
            We'll take you directly to our Google page where you can write your review. 
            It helps other customers find us and would mean the world to us!
          </p>

          <!-- Buttons -->
          <div class="space-y-3">
            <button
              @click="goToGoogleReview"
              class="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-xl shadow-lg active:scale-95 transition-all duration-200 flex items-center justify-center"
            >
              <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              ⭐ Write Google Review
            </button>
            
            <button
              @click="closePopup"
              class="w-full py-3 px-4 bg-gray-100 text-gray-600 font-medium rounded-xl active:scale-95 transition-all duration-200"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useFeedbackStore } from '@/stores/feedback'
import { submitPositiveFeedback, trackReviewClick as trackReviewClickAPI, submitPhoneNumber as submitPhoneNumberAPI } from '@/services/api'

const router = useRouter()
const store = useFeedbackStore()

// Reactive variables
const phoneNumber = ref('')
const isSubmittingPhone = ref(false)
const phoneSubmitted = ref(false)
const reviewClicked = ref(false)
const reviewPlatform = ref('')
const showReviewPopup = ref(false)

const customerName = computed(() => store.customerName || 'Welcome!')
const aiResponse = computed(() => store.feedback.aiResponse)

const shouldShowPhoneInput = computed(() => {
  // Only show phone input for anonymous users (no customer name or Anonymous/Welcome)
  return !store.customerName || store.customerName === 'Anonymous' || store.customerName === 'Welcome!'
})

const expiryDate = computed(() => {
  const date = new Date()
  date.setMonth(date.getMonth() + 5)
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })
})

// Determine which Google review URL to use based on sales person
const googleReviewUrl = computed(() => {
  // Debug: log store values
  console.log('🔍 DEBUG - store.customer:', store.customer)
  console.log('🔍 DEBUG - salesPersonName:', store.customer.salesPersonName)
  console.log('🔍 DEBUG - employeeName:', store.customer.employeeName)
  
  // Default: Service Center Google Review
  let url = 'https://g.page/r/CbWWXBd9rLXXEAE/review'  // Updated Service Center URL
  
  // If this came from a sales person link, use sales Google review page
  if (store.customer.salesPersonName) {
    url = 'https://g.page/r/CYtn30uN7-zZEAI/review'  // Sales Google Review
    console.log('🏆 SALES DETECTED - Using Sales Google Review URL')
  } else {
    console.log('🔧 DEFAULT - Using Service Center Google Review URL')
  }
  
  return url
})

// Submit positive feedback on page load to get caseNumber
onMounted(async () => {
  // Check if feedback was already submitted (caseNumber exists)
  if (store.caseNumber) {
    console.log('✅ Using existing case number for review tracking:', store.caseNumber)
    
    // If customer is identified (not anonymous), send email immediately
    if (!shouldShowPhoneInput.value) {
      await sendEmailImmediately()
    }
    
    // Show popup immediately
    showReviewPopup.value = true
    return
  }
  
  // Only submit if no caseNumber exists (fallback for direct navigation)
  try {
    console.log('📝 Fallback: Submitting positive feedback...')
    const response = await submitPositiveFeedback(store.customer, store.feedback)
    
    if (response.caseNumber) {
      store.setCaseNumber(response.caseNumber)
      console.log('✅ Case number stored:', response.caseNumber)
      
      // If customer is identified (not anonymous), send email immediately
      if (!shouldShowPhoneInput.value) {
        await sendEmailImmediately()
      }
    }
  } catch (error) {
    console.error('❌ Failed to submit positive feedback:', error)
  }
  
  // Show popup immediately
  showReviewPopup.value = true
})

// Send email immediately for identified customers
async function sendEmailImmediately() {
  try {
    console.log('📧 Sending positive email immediately for identified customer:', store.customerName)
    
    // Call the phone API but without actual phone number requirement
    await submitPhoneNumberAPI({
      caseNumber: store.caseNumber || '',
      phoneNumber: 'N/A - Customer Identified',
      customerName: store.customerName || 'Anonymous',
      customer: store.customer,
      feedback: store.feedback,
      reviewClicked: reviewClicked.value,
      reviewPlatform: reviewPlatform.value || null
    })
    
    console.log('✅ Positive email sent successfully for identified customer')
  } catch (error) {
    console.error('❌ Failed to send positive email:', error)
  }
}

async function submitPhoneNumber() {
  if (!phoneNumber.value.trim()) return
  
  isSubmittingPhone.value = true
  
  try {
    console.log('📱 Submitting phone number and sending positive email:', phoneNumber.value)
    
    // Call the phone API which will now send the email
    await submitPhoneNumberAPI({
      caseNumber: store.caseNumber || '',
      phoneNumber: phoneNumber.value,
      customerName: store.customerName || 'Anonymous',
      customer: store.customer,
      feedback: store.feedback,
      reviewClicked: reviewClicked.value,
      reviewPlatform: reviewPlatform.value || null
    })
    
    phoneSubmitted.value = true
    console.log('✅ Phone number submitted and positive email sent successfully')
    
    // Track analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'phone_submitted', {
        customer: store.customerName,
        caseNumber: store.caseNumber
      })
    }
  } catch (error) {
    console.error('❌ Failed to submit phone number:', error)
  } finally {
    isSubmittingPhone.value = false
  }
}

async function trackReviewClick(platform: 'google' | 'yelp') {
  try {
    console.log(`🌟 User clicked ${platform} review button`)
    
    // Mark review as clicked for follow-up email
    reviewClicked.value = true
    reviewPlatform.value = platform
    
    await trackReviewClickAPI({
      platform,
      caseNumber: store.caseNumber || undefined,
      customerName: store.customerName || 'Anonymous',
      timestamp: new Date().toISOString(),
      feedbackRating: store.feedback.rating,
      feedbackText: store.feedback.text || 'Customer left a positive review'
    })
    
    console.log(`✅ ${platform} review click tracked successfully`)
    
    // Track analytics safely
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'review_click', {
        platform: platform,
        customer: store.customerName,
        caseNumber: store.caseNumber
      })
    }
  } catch (error) {
    console.error('❌ Failed to track review click:', error)
  }
}

function handleMaybeLater() {
  router.push('/thank-you')
}

function handleNoThanks() {
  router.push('/thank-you')
}

// Popup functions
function closePopup() {
  showReviewPopup.value = false
}

function goToGoogleReview() {
  // Track the click first
  trackReviewClick('google')
  
  // Close popup
  showReviewPopup.value = false
  
  // Log which Google review page we're opening
  if (store.customer.salesPersonName) {
    console.log('🏆 Redirecting to Sales Google Review page for:', store.customer.salesPersonName)
  } else {
    console.log('🔧 Redirecting to Service Center Google Review page (default)')
  }
  
  // Open the appropriate Google review page using computed URL
  window.open(googleReviewUrl.value, '_blank', 'noopener,noreferrer')
}


</script> 